import React, { useContext, useState } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { TestCaseFlowDataContext } from "../../main-pages/TestcaseGenerationPage";
import { useLocation, useNavigate } from "react-router-dom";

const UserStoryComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDownload, setOpenDownload] = useState(false);
  const { userStory } = useContext(TestCaseFlowDataContext);
  const [activeThumb, setActiveThumb] = useState(null);
  const headings = [
    "Title",
    "Description",
    "Steps",
    "Acceptance Criteria",
    "Additional Information",
  ];
  <Typography component="div" sx={{ whiteSpace: "pre-wrap" }}>
    {userStory.split("\n").map((line, index, arr) => {
      const trimmedLine = line.trim();
      const nextLine = arr[index + 1]?.trim();
      const isPredefinedHeading = headings.some(
        (heading) => trimmedLine.toLowerCase() === heading.toLowerCase()
      );
      const isDynamicHeading =
        !isPredefinedHeading && trimmedLine.length <= 30 && nextLine;
      const hasColon = trimmedLine.includes(":");
      const isHeading = isPredefinedHeading || isDynamicHeading || hasColon;
      let displayText = trimmedLine;
      if (hasColon) {
        const [heading, ...content] = trimmedLine.split(":");
        displayText = (
          <>
            <span style={{ fontWeight: "bold", color: "#888" }}>
              {heading}:
            </span>{" "}
            <span style={{ color: "#000" }}>{content.join(":").trim()}</span>
          </>
        );
      }

      return (
        <Typography
          key={index}
          sx={{
            color: isHeading && !hasColon ? "#888" : "#000",
            fontWeight: isHeading ? "bold" : "normal",
            marginTop: isHeading ? 2 : 0,
            marginBottom: isHeading ? 1 : 2,
          }}
        >
          {displayText}
        </Typography>
      );
    })}
  </Typography>;

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const EditableField = ({ label, initialValue, textProps }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);
    const [tempValue, setTempValue] = useState(initialValue);

    const handleSave = (label) => {
      setValue(tempValue); // Save the edited value
      const updatedUserStory = { ...userStory, [label]: tempValue };
      navigate("/dashboard/generate-testcases/output", {
        state: { ...location.state, userStory: updatedUserStory },
        replace: true,
      });
      setIsEditing(false);
    };

    const handleCancel = () => {
      setTempValue(value);
      setIsEditing(false);
    };

    return (
      <Box
        sx={{
          "&:hover .edit-icon": { visibility: "visible" },
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ marginBottom: 1, fontSize: "14px" }}
        >
          {label}
        </Typography>
        {isEditing ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              name={label}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              sx={{ marginTop: 1, marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
            <Box>
              {/* Save Button */}
              <Button
                onClick={() => handleSave(label)}
                variant="outline"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Save
              </Button>
              {/* Cancel Button */}
              <Button
                onClick={handleCancel}
                variant="outline"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography variant="body1" {...textProps}>
              {value}
            </Typography>
            <IconButton
              onClick={() => setIsEditing(true)}
              className="edit-icon"
            >
              <PenIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  const accordionContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        width: "100%",
      }}
    >
      {typeof userStory === "object" && userStory !== null ? (
        <>
          <Box sx={{ width: "100%" }}>
            <EditableField
              label="Title"
              initialValue={userStory["Title"]}
              textProps={{ sx: { fontSize: "14px", marginBottom: 3 } }}
            />
          </Box>

          <Box sx={{ width: "100%" }}>
            <EditableField
              label="Description"
              initialValue={userStory["Description"]}
              textProps={{ sx: { fontSize: "14px", marginBottom: 3 } }}
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <EditableField
              label="Acceptance Criteria"
              initialValue={userStory["Acceptance Criteria"]}
              textProps={{ sx: { fontSize: "14px", marginBottom: 3 } }}
            />
          </Box>
          {userStory["Additional Information"] && (
            <Box sx={{ width: "100%" }}>
              <EditableField
                label="Additional Information"
                initialValue={userStory["Additional Information"]}
                textProps={{ sx: { fontSize: "14px", marginBottom: 3 } }}
              />
            </Box>
          )}
        </>
      ) : (
        <Typography component="div" sx={{ whiteSpace: "pre-wrap" }}>
          {userStory.split("\n").map((line, index) => {
            const trimmedLine = line.trim();
            const isPredefinedHeading = headings.some((heading) =>
              trimmedLine.toLowerCase().startsWith(heading.toLowerCase())
            );
            const isHeading = isPredefinedHeading;
            return isHeading ? (
              <Typography
                key={index}
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  marginTop: 3,
                  marginBottom: 1,
                  fontSize: "14px",
                }}
              >
                {trimmedLine}
              </Typography>
            ) : (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "text.primary",
                  marginLeft: "0",
                }}
              >
                {trimmedLine}
              </Typography>
            );
          })}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", marginTop: 2 }}>
        {/* Thumbs Up Button with Tooltip */}
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#49A3FF",
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        {/* Thumbs Down Button with Tooltip */}
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#FF4842",
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
        <CommonCopy contentToCopy={userStory} />
      </Box>
    </Box>
  );

  const buttonsgroup = [
    {
      label: "Download",
      variant: "text",
      startIcon: <FileDownloadOutlinedIcon />,
      onClick: () => setOpenDownload(true),
    },
  ];

  return (
    <Box>
      <CommonAccordion
        summary={
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            User Story 
          </Typography>
        }
        details={accordionContent}
        buttons={buttonsgroup}
        type={"output"}
      />
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceResponse={[
          { heading: "User Story", type: "text", data: userStory },
        ]}
        serviceName="User Story"
        wordData={[{ heading: "User Story", type: "text", data: userStory }]}
        documents={["docx", "pdf"]}
      />
    </Box>
  );
};

export default UserStoryComponent;
